import { ChatOutlined, ExpandMore } from '@mui/icons-material';
import { Divider, SelectChangeEvent, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chat } from '@/console/features/factory-operator/components/chat/chat';
import { NEW_CHAT } from '@/console/features/factory-operator/factory-operator.constants';
import { ChatContainer } from '@/console/features/factory-operator/factory-operator.styled';
import { AddIcon } from '@/shared/assets/icons/add-icon';
import { IconBgButton } from '@/shared/components/ui/button/icon-bg-button/icon-bg-button';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { StyledSelect } from '@/shared/components/ui/styled-select/styled-select';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag/use-feature-flag';
import { FEATURE_FACTORY_OPERATOR } from '@/shared/hooks/use-feature-flag/use-feature-flag.constants';
import { useReadAllChatsQuery } from '@/shared/redux/api/sdai/sdai-api';
import { ChatFragment } from '@/shared/types/types.generated';

export const FactoryOperator = () => {
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const { t } = useTranslation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string>(NEW_CHAT);

  const { data, isLoading } = useReadAllChatsQuery();
  const chats = data?.readAllChats.items;

  const handleToggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  const handleChatSelection = (event: SelectChangeEvent<unknown>) => {
    setSelectedChatId(event.target.value as string);
  };

  const formatChatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('notifications:today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('notifications:yesterday');
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year:
          date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const getChatName = (chat: ChatFragment) => {
    if (chat.history && chat.history.length > 0) {
      return chat.history?.[0].message?.substring(0, 30) + '...';
    }
    return t('factory_operator:chat_from', {
      timestamp: formatChatDate(chat.creationTimestamp),
    });
  };

  if (!isFeatureFlagEnabled(FEATURE_FACTORY_OPERATOR)) {
    return null;
  }

  return (
    <DrawerController
      title={t('factory_operator:chat_header')}
      showDivider={false}
      width="600px"
      trigger={
        <ChatContainer>
          <IconBgButton variant="primary" onClick={handleToggleChat}>
            <ChatOutlined />
          </IconBgButton>
        </ChatContainer>
      }
    >
      {() => (
        <Stack gap="1rem" height="100%">
          <StyledSelect
            value={selectedChatId}
            onChange={handleChatSelection}
            IconComponent={ExpandMore}
            displayEmpty
            renderValue={(value) => {
              if (value === NEW_CHAT) {
                return <span>{t('factory_operator:new_conversation')}</span>;
              }
              const chat = chats?.find((c: ChatFragment) => c.chatId === value);
              return chat
                ? getChatName(chat)
                : t('factory_operator:select_a_chat');
            }}
          >
            <MenuItem value={NEW_CHAT} icon={<AddIcon />}>
              {t('factory_operator:new_conversation')}
            </MenuItem>

            {isLoading && (
              <MenuItem disabled>
                {t('factory_operator:loading_chats')}
              </MenuItem>
            )}
            {chats && chats.length > 0 && <Divider />}
            {chats?.map((chat: ChatFragment) => (
              <MenuItem
                key={chat.chatId}
                value={chat.chatId}
                additionalInfo={
                  <Typography variant="p10" color="textSecondary">
                    {formatChatDate(chat.creationTimestamp)}
                  </Typography>
                }
              >
                {getChatName(chat)}
              </MenuItem>
            ))}
          </StyledSelect>
          <Chat chatId={selectedChatId} setCurrentChatId={setSelectedChatId} />
        </Stack>
      )}
    </DrawerController>
  );
};
